import React, { useState, useMemo } from "react";
import { Box, Button, DialogContentText } from "@mui/material";
import { useFormContext } from "react-hook-form";

import { AlertDialog, ControlledFormEditor } from "../../../components";
import { removeAllMarkTags } from "../../../services/contentUtils";
import { useStore } from "../../../services/hooks";
import { getTaskPermissions } from "../../../services/utils";

const PassageReferenceContentCard = ({ activeTasks, readonly }) => {
  const { control, getValues, setValue, watch } = useFormContext();
  const passageReferenceContent = watch("passage.referenceContent");

  const [alertDialogProps, setAlertDialogProps] = useState({ open: false });

  const [passage] = useStore("passage");

  const createReferenceContent =
    ([content, comments, suggestions]) =>
    () => {
      setValue("passage.referenceContent", removeAllMarkTags(content), {
        shouldDirty: true,
      });
      setValue("passage.referenceContentComments", comments, {
        shouldDirty: true,
      });
      setValue("passage.referenceContentSuggestions", suggestions, {
        shouldDirty: true,
      });
    };

  const permissions = useMemo(
    () => getTaskPermissions(activeTasks.passage),
    [activeTasks.passage],
  );

  const onReady = (editor) => {
    console.info("Passage Reference Content editor is ready");
    editor.on("cde:deleteReferenceContent", () => {
      setAlertDialogProps({
        confirmButtonText: "Delete",
        maxWidth: "xs",
        message: (
          <DialogContentText>{`Delete reference passage?`}</DialogContentText>
        ),
        onClose: (confirmed) => {
          setAlertDialogProps({ open: false });
          if (!confirmed) return;
          setValue("passage.referenceContent", "", { shouldDirty: true });
        },
        open: true,
        dialogTitle: `Delete Reference Passage`,
      });
    });
  };

  if (!passage || !getValues("passage.id")) return null;

  return (
    <>
      {!readonly && !passageReferenceContent && (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "row",
            flexGrow: 1,
            justifyContent: "center",
          }}
        >
          <Button
            color="primary"
            disabled={permissions.includes("suggestChanges")}
            onClick={createReferenceContent(
              getValues([
                "passage.content",
                "passage.contentComments",
                "passage.contentSuggestions",
              ]),
            )}
            size="small"
            variant="contained"
          >
            Create Reference Passage
          </Button>
        </Box>
      )}
      {passageReferenceContent && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            overflowX: "hidden",
            overflowY: "auto",
            width: 650,
            '& .ck.ck-editor[role="application"]': {
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              '& .ck.ck-editor__main[role="presentation"]': {
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                height: 100,
                '& .ck.ck-content.ck-editor__editable[role="textbox"]': {
                  flexGrow: 1,
                  "& .qti-visually-hidden": {
                    display: "none",
                  },
                  "& figure.table": {
                    marginLeft: "0px",
                  },
                },
              },
            },
          }}
        >
          <ControlledFormEditor
            assets={{
              images: passage.imageAssets,
              medias: passage.mediaAssets,
            }}
            control={control}
            comments={"passage.referenceContentComments"}
            id="passage.referenceContent"
            name="passage.referenceContent"
            onReady={onReady}
            options={[
              "referenceContent",
              permissions.includes("suggestChanges") && "suggestChanges",
              permissions.includes("acceptChanges") && "acceptChanges",
            ].filter(Boolean)}
            readonly={readonly}
            suggestions={"passage.referenceContentSuggestions"}
            type="passage"
          />
        </Box>
      )}
      {alertDialogProps.open && <AlertDialog {...alertDialogProps} />}
    </>
  );
};

export { PassageReferenceContentCard };
